import { useEffect, useRef, type ReactNode } from 'react';

export function BudsLogo() {
  return (
    <img
      src="/buds-logo.svg"
      alt="Buds Rewards"
      width={132}
      height={40}
      className="h-10 w-auto"
    />
  );
}

interface AuthCardProps {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * The single surface every auth screen renders inside
 * (docs/auth/35-design/components.md): brand logo, gradient accent, then the
 * step's content.
 */
export function AuthCard({ title, subtitle, children, footer }: AuthCardProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  const card = useRef<HTMLElement>(null);
  const firstRender = useRef(true);

  /**
   * Move focus to the heading when the step changes.
   *
   * Steps swap in place at one URL, so without this a screen reader user is
   * told nothing — focus stays wherever the control they just used was, or
   * falls to `<body>` when that control unmounts, and the flow silently
   * continues somewhere they cannot see
   * (docs/auth/35-design/accessibility.md).
   *
   * The heading is the FALLBACK, not the override. A step whose first field
   * autofocuses has already put focus somewhere better — the user can just
   * type — and stealing it back to a heading would cost them a Tab and break
   * keyboard-only sign-in. So this only fires when nothing inside the card
   * claimed focus: the consent screen, the post-login offers, the error card.
   *
   * Keyed on the title, not on every render, so an inline error inside a step
   * does not yank focus away from the field being corrected.
   */
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const active = document.activeElement;
    const claimed =
      active && active !== document.body && card.current?.contains(active);
    if (!claimed) heading.current?.focus();
  }, [title]);

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <BudsLogo />
        </div>

        <section
          ref={card}
          className="overflow-hidden rounded-2xl bg-buds-surface shadow-[0_4px_24px_rgba(0,48,32,0.12)]"
        >
          <div className="buds-gradient-accent h-1" />
          <div className="px-7 py-8">
            {/* A programmatic focus target, not a tab stop — hence -1 and no
                indicator of its own. */}
            <h1
              ref={heading}
              tabIndex={-1}
              className="step-heading text-2xl font-bold text-buds-dark"
            >
              {title}
            </h1>
            {/* break-words: subtitles interpolate email addresses and client
                names — single unbreakable tokens under UAX #14 — and the card
                is overflow-hidden, so without it a long address is clipped
                mid-word with no ellipsis on exactly the screen whose job is
                "check this mailbox". */}
            {subtitle ? (
              <p className="mt-2 break-words text-sm leading-relaxed text-buds-muted">
                {subtitle}
              </p>
            ) : null}
            <div className="mt-6">{children}</div>
          </div>
        </section>

        {footer ? <div className="mt-6">{footer}</div> : null}
      </div>
    </main>
  );
}
